import React, { useEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import clsx from 'clsx';
import moment from 'moment/moment';
import { NumericMetricInfo, TagMetricInfo, TagMetricEnum } from '@moderepo/mode-apis';
import { MultiSeriesDataPoint, Time, ChartSettings, ModeTheme } from '@moderepo/mode-app-components';
import { useTheme } from '@material-ui/core';



/**
 * Predefined set of colors for series
 */
const colorSet = (new am4core.ColorSet());
colorSet.saturation = 0.6;

// Limit the maximum number of random colors
const MAX_COLOR_SET = 100;


/**
 * One problem of randomly pick a series is that every time the chart re-render, the series will look different and that might confuse the user.
 * So to solve this problem, we will pick a style base on the hash value of the series name. This mean we will also return the same style for
 * the same series name. So it is still randomly pick a style but not totally random.
 */
const getRandomColor = (metricInfo: NumericMetricInfo | TagMetricInfo | undefined, dataValue: number | string | null): am4core.Color => {
    // Calculate a hashValue for the series name. This is just a simple hash algorithm. All it does is add the all char code value
    // of each letter in the series name.
    if (!metricInfo) {
        return colorSet.getIndex(0);
    }

    const isTagMetric = (metricInfo as TagMetricInfo).enumDefinitions !== undefined;
    const tagMetricInfo = metricInfo as TagMetricInfo;


    if (isTagMetric && tagMetricInfo.enumDefinitions && tagMetricInfo.enumDefinitions.length > 0) {
        // If metricInfo is a TagMetricInfo and if metricInfo has enumDefinitions, we can return a different color base on the dataValue
        const mappedValueIndex = tagMetricInfo.enumDefinitions.findIndex((mappedValue: TagMetricEnum) => {
            return mappedValue.name === dataValue || mappedValue.value === dataValue;
        });

        if (mappedValueIndex >= 0) {
            const metricName = metricInfo.name;
            let hashValue = 0;
            for (let i = 0; i < metricName.length; i += 1) {
                hashValue += (metricName.charCodeAt(i) * i);
            }

            const baseColor = colorSet.getIndex(hashValue % MAX_COLOR_SET);

            // The brightness for each map value will depends on the index of the tagged value AND the total # of mapped values.
            // The we don't want to make the bar very bright that it looks like white so we make sure the MAXIMUM brightness is 0.8 or 80%
            // Then if a metric has 4 mapped values then EACH mapped value will have 0.8 / 4 = 0.2 or 20%.
            // Then the brightness of a value will be based on the index it is in the enumDefinitions array. If the value is at index 3 then it will
            // have 0.2 * 3 = 60% brightness.
            const brightnessPerMapValue = (0.5 / tagMetricInfo.enumDefinitions.length);
            const brightenAmount = (mappedValueIndex * brightnessPerMapValue);

            return baseColor.brighten(-brightenAmount);
        }
    }

    // When metric's value don't have any mapping then we have to use the metric's name + value to pick a random color
    const metricName = `${metricInfo.name}:${dataValue}`;
    let hashValue = 0;
    for (let i = 0; i < metricName.length; i += 1) {
        hashValue += (metricName.charCodeAt(i) * i);
    }
    return colorSet.getIndex(hashValue % MAX_COLOR_SET);
};


const fullDateFormat = {
    month : 'short',
    day   : '2-digit',
    year  : 'numeric',
    hour  : '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
} as Intl.DateTimeFormatOptions;


/**
 * Sometime when the data resolution is very long e.g. 1 data point per month, it is not necessary to show the data point's time stamp with all the
 * time details e.g. hh:mm:ss. So, we will have different date/time format for different data resolution.
 */
const dateFormatByDataResolution = {
    short: {
        year  : 'numeric',
        month : 'short',
        day   : '2-digit',
        hour  : '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    } as Intl.DateTimeFormatOptions,
    medium: {
        year  : 'numeric',
        month : 'short',
        day   : '2-digit',
        hour  : '2-digit',
        minute: '2-digit',
        hour12: false,
    } as Intl.DateTimeFormatOptions,
    long: {
        year  : 'numeric',
        month : 'short',
        day   : '2-digit',
        hour12: false,
    } as Intl.DateTimeFormatOptions,
    extraLong: {
        year  : 'numeric',
        month : 'short',
        hour12: false,
    } as Intl.DateTimeFormatOptions,
};


const shortDateFormat = {
    year  : 'numeric',
    month : '2-digit',
    day   : '2-digit',
    hour12: false,
} as Intl.DateTimeFormatOptions;



export interface GanttChartProps extends ChartSettings {
    readonly chartId: string;
    readonly dataPoints: readonly MultiSeriesDataPoint<number | string>[];

    // dataPoints contains data for many series. However, we don't need to show them all in this chart.
    // This 'displayMetrics' array is used for specifying which series we need to display in the chart.
    readonly displayMetrics: readonly (NumericMetricInfo | TagMetricInfo)[];

    readonly showCursorAt?: number | undefined;         // The position (timestamp) on the X axis where we want to show a cursor
    readonly disableZoom?: boolean;
    readonly dateRange: {
        readonly start: number,
        readonly end: number,
        readonly resolution?: number | undefined
    }
}



export const GanttChart: React.FC<GanttChartProps> = (props: GanttChartProps) => {

    const { onChartZoomAndPan, onChartClick } = props;
    const chartRef = useRef<am4charts.XYChart>();
    const theme = useTheme() as ModeTheme;


    // For debugging chart re-rendering
    const chartIdRef = useRef<string>();
    const showCursorAtRef = useRef<number | undefined>();
    const disableZoomRef = useRef<boolean | undefined>();
    const displayMetricsRef = useRef<readonly(NumericMetricInfo | TagMetricInfo)[]>();
    const localeRef = useRef<string | undefined>();
    const onChartZoomAndPanRef = useRef<(startTS: number, endTS: number)=> void>();
    const onChartClickRef = useRef<(timestamp: number)=> void>();
    const dataPointsRef = useRef<readonly MultiSeriesDataPoint<number | string>[]>();
    const dataResolutionRef = useRef<number | undefined>();


    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.XYChart;
            if (chart && !chart.isDisposed()) {
                // console.log('dispose chart');

                // unsubscribe to data axis's events
                const dateAxis = chart.xAxes.values[0] as am4charts.DateAxis;
                dateAxis.events.off('selectionextremeschanged');
                chart.events.off('ready');
                chart.plotContainer.events.off('hit');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };

    }, []);



    /**
     * This useEffect is used for creating/updating the chart. This useEffect depends on a lot of props and when each props change, this useEffect
     * will be called. So, it will be very slow if we were to re-create the chart every time this useEffect is called. We can't remove any of the
     * dependencies because the chart needs all the dependencies. So to solve this problem, we will keep a reference of the PREVIOUS prop values.
     * Each time this useEffect is called, we will compare the new prop values with the previous to determine which props changed. Some props
     * will always require a new chart to be created e.g. chartId and displayedSeries when they are changed. Some props does not which mean we can
     * just update the chart's properties when component props changed.
     */
    useEffect(() => {

        // console.log('Chart useEffect');
        let chart = chartRef.current as am4charts.XYChart;

        // We will need to re-create chart if chartId changed or displaySeries changed
        const createNewChart: boolean = chartIdRef.current !== props.chartId
            || displayMetricsRef.current !== props.displayMetrics;

        if (!chart || createNewChart) {

            if (chart && !chart.isDisposed()) {
                // Dispose old chart first
                // console.log('dispose chart');

                // unsubscribe to data axis's events
                (chart.xAxes.values[0] as am4charts.DateAxis).events.off('selectionextremeschanged');
                chart.events.off('ready');
                chart.plotContainer.events.off('hit');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }

            // create new Chart
            // console.log('create chart', props.chartId);
            chart = am4core.create(props.chartId, am4charts.XYChart);
            chart.paddingRight = 30;
            // chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm';

            chartRef.current = chart;
            chart.dateFormatter.dateFormat = fullDateFormat;

            // add cursor
            chart.cursor = new am4charts.XYCursor();
            chart.cursor.lineX.strokeWidth = 2;
            chart.cursor.lineY.disabled = true;
            chart.cursor.opacity = 1;
            chart.cursor.lineX.stroke = am4core.color(theme.palette.chart.cursorStroke);
            chart.plotContainer.background.stroke = am4core.color(theme.palette.chart.gridStroke);

            const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
            dateAxis.baseInterval = { count: 1, timeUnit: 'second' };
            dateAxis.strictMinMax = true;
            dateAxis.renderer.tooltipLocation = 0;
            dateAxis.renderer.minGridDistance = 100;
            dateAxis.dateFormats.setKey('day', shortDateFormat);                 // The value displayed in each tick
            dateAxis.periodChangeDateFormats.setKey('day', shortDateFormat);     // The first value
            dateAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
            dateAxis.renderer.grid.template.stroke = am4core.color(theme.palette.chart.gridStroke);


            const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = 'name';
            categoryAxis.renderer.grid.template.location = 0;
            categoryAxis.renderer.inversed = true;
            categoryAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
            categoryAxis.renderer.grid.template.stroke = am4core.color(theme.palette.chart.gridStroke);
            if (categoryAxis.tooltip) {
                categoryAxis.tooltip.disabled = true;
            }

            const series1 = chart.series.push(new am4charts.ColumnSeries());
            series1.columns.template.width = am4core.percent(80);
            series1.columns.template.tooltipText = '{value}: {openDateX} - {dateX}';

            series1.dataFields.openDateX = 'fromDate';
            series1.dataFields.dateX = 'toDate';
            series1.dataFields.categoryY = 'name';
            series1.columns.template.propertyFields.fill = 'color'; // get color from data
            series1.columns.template.propertyFields.stroke = 'color';
            series1.columns.template.strokeOpacity = 1;

            const bullet = new am4charts.LabelBullet();
            bullet.label.text = '{value}';
            bullet.label.horizontalCenter = 'middle';
            series1.bullets.push(bullet);

            if (chart.legend) {
                chart.legend.labels.template.fill = am4core.color(theme.palette.text.primary);
            }

            // subscribe a new handler
            if (onChartZoomAndPan) {
                chart.events.on('ready', () => {
                    (chart.xAxes.values[0] as am4charts.DateAxis).events.on('selectionextremeschanged', (event: any) => {
                        const start = moment(event.target.minZoomed).valueOf();
                        const end = moment(event.target.maxZoomed).valueOf();
                        onChartZoomAndPan(start, end);
                    });
                });
            }
        }


        // data resolution changed, we need to update the dateAxis's tooltip date format accordingly
        const dateAxis = chart.xAxes.values[0] as am4charts.DateAxis;
        if (createNewChart || dataResolutionRef.current !== props.dateRange.resolution) {
            // Default, show full date/time
            dateAxis.tooltipDateFormat = dateFormatByDataResolution.short;

            if (props.dateRange.resolution) {
                if (props.dateRange.resolution >= Time.MONTH_IN_MS) {
                    dateAxis.tooltipDateFormat = dateFormatByDataResolution.extraLong;
                } else if (props.dateRange.resolution >= Time.DAY_IN_MS) {
                    dateAxis.tooltipDateFormat = dateFormatByDataResolution.long;
                } else if (props.dateRange.resolution >= Time.MINUTE_IN_MS) {
                    dateAxis.tooltipDateFormat = dateFormatByDataResolution.medium;
                }
            }
        }


        // If we just created a new chart or if locale changed, update chart date formatter locale
        if (createNewChart || localeRef.current !== props.locale) {
            if (props.locale) {
                chart.dateFormatter.intlLocales = props.locale;
            }
        }


        // If we are creating new chart or if showCursorAt props changed, update chart's cursor
        if ((createNewChart || showCursorAtRef.current !== props.showCursorAt) && props.showCursorAt !== undefined) {
            // Create cursor at the specified location, props.showCursorAt
            const point = dateAxis.valueToPoint(props.showCursorAt) || 0;
            chart.cursor.triggerMove(point, 'soft');
        }


        // If we are creating new chart or if disableZoom prop changed
        if (createNewChart || disableZoomRef.current !== props.disableZoom) {
            // disable zoom/pan
            if (props.disableZoom) {
                chart.cursor.behavior = 'none';
            } else {
                chart.cursor.behavior = 'zoomX';
            }
        }


        // if we just created a new chart or the data changed, replace chart's data with new data
        if (createNewChart || dataPointsRef.current !== props.dataPoints || displayMetricsRef.current !== props.displayMetrics) {
            // changing data will trigger zoom/pan events which will trigger another data update and cause an infinite loop.
            // so before we make changes to the data, we will disable dateAxis event. Once data is rendered, we re-enable the event.
            dateAxis.events.disable();
            chart.events.on('datavalidated', () => {
                dateAxis.events.enable();
                chart.events.off('datavalidated');
            });


            /**
             * Convert MultiSeriesDataPoint[] to a format that Chart can understand like this
             *      [{
             *          name    : 'state',
             *          value   : 'off',
             *          fromDate: 1611338565000,
             *          toDate  : 1611338566000,
             *          color   : colorSet.getIndex(8).brighten(0),
             *      }, {
             *          name    : 'state',
             *          value   : 'on',
             *          fromDate: 1611338566000,
             *          toDate  : 1611338567000,
             *          color   : colorSet.getIndex(8).brighten(0),
             *      }, {
             *          name    : 'error',
             *          value   : 'warning',
             *          fromDate: 1611338560000,
             *          toDate  : 1611338565000,
             *          color   : colorSet.getIndex(8).brighten(0),
             *      }, {
             *          name    : 'error',
             *          value   : 'normal',
             *          fromDate: 1611338565000,
             *          toDate  : 1611338569000,
             *          color   : colorSet.getIndex(8).brighten(0),
             *      }]
             */
            const metricsDataMap: {
                [metricName: string]: {
                    name: string;
                    value: number | string | null;
                    fromDate: number;
                    toDate: number;
                    color: am4core.Color;
                }[];
            } = {};
            props.dataPoints.forEach((multiSeriesDataPoint: MultiSeriesDataPoint<number | string>) => {
                Object.entries(multiSeriesDataPoint.values).forEach(([metricName, value]) => {


                    // find the metric's data from the metricsDataMap
                    const metricData = metricsDataMap[metricName];
                    if (metricData && metricData.length > 0) {
                        // If we found metricData then we just need to update the metricData.
                        const lastItem = metricData[metricData.length - 1];
                        if (lastItem.value === value) {
                            // If the 'value' is the same as the latest value from metricData then update the last item's toDate
                            lastItem.toDate = multiSeriesDataPoint.dateValue;
                        } else {
                            // If the 'value' changed then add a new data point to the metricData. But we first need to update the previous item's
                            // toDate to the new data point' date.

                            // update last item's toDate
                            lastItem.toDate = multiSeriesDataPoint.dateValue;


                            // Add new item
                            const metricInfo = props.displayMetrics.find((metric: NumericMetricInfo | TagMetricInfo) => {
                                return metric.name === metricName;
                            });
                            const color = getRandomColor(metricInfo, value);
                            metricData.push({
                                name    : lastItem.name,
                                value,
                                fromDate: multiSeriesDataPoint.dateValue,
                                toDate  : multiSeriesDataPoint.dateValue + 1000,    // Add 1 second to toDate or else the chart won't render
                                color,
                            });
                        }
                    } else {
                        const metricInfo = props.displayMetrics.find((metric: NumericMetricInfo | TagMetricInfo) => {
                            return metric.name === metricName;
                        });

                        if (metricInfo) {
                            // We found a metricName that does not exist in dataPointsByMetric yet, create a new one ONLY if the metric name is
                            // one of the metrics in the props.displayMetrics
                            const color = getRandomColor(metricInfo, value);
                            const newMetricData = {
                                name    : metricInfo.displayName || metricName,
                                value,
                                fromDate: multiSeriesDataPoint.dateValue,
                                toDate  : multiSeriesDataPoint.dateValue + 1000,    // Add 1 second to toDate or else the chart won't render
                                color,
                            };
                            metricsDataMap[metricName] = [newMetricData];
                        }
                    }
                });
            });

            // convert metricsDataMap back to array of data and set it to chart.data
            chart.data = Object.values(metricsDataMap).flat().filter((item) => {
                return item.value !== null;
            });
        }


        // If we just created a new chart and the container has a onChartClick event handler, listen to the 'hit' event on the chart's plot area
        // and dispatch an event when it is clicked.
        if (onChartClick && (createNewChart || onChartClickRef.current !== onChartClick)) {
            chart.plotContainer.events.off('hit');
            chart.plotContainer.events.on('hit', (event: any) => {
                const point = event.spritePoint;                            // get the point the user clicked in pixel
                const timestamp = Math.floor(dateAxis.xToValue(point.x));   // convert the point to timestamp in millisecond
                onChartClick(timestamp);
            });
        }


        // console.log('End useEffect');


        chartIdRef.current = props.chartId;
        showCursorAtRef.current = props.showCursorAt;
        disableZoomRef.current = props.disableZoom;
        displayMetricsRef.current = props.displayMetrics;
        localeRef.current = props.locale;
        onChartZoomAndPanRef.current = onChartZoomAndPan;
        onChartClickRef.current = onChartClick;
        dataPointsRef.current = props.dataPoints;
        dataResolutionRef.current = props.dateRange.resolution;
    }, [props.chartId, props.dataPoints, props.displayMetrics, props.locale, props.showCursorAt, props.dateRange.resolution, onChartZoomAndPan,
        onChartClick, props.disableZoom, theme]);



    return (
        <div id={props.chartId} className={clsx(props.className)} />
    );
};
